import { Sensor } from './sensor';

export class OTObject {
  public objectID!: number;
  public objectName!: string;
  public objectTypeID!: number;
  public objectType!: string;
  public parentID!: number;
  public objectStatus!: string;
  public colorCode!: string;
  public description!: string;
  public isAsset!: boolean;
  public isConnected!: boolean;
  public isLifetimeConnected?: boolean; // EM: used to track if equipment was ever connected
  public warning!: boolean;
  public critical!: boolean;
  public inactive?: boolean; // EM: sensor/asset inactive status
  public isArchived?: boolean; // EM: equipment archive status
  public equipmentTypeID?: number;
  public equipmentType!: string;
  public childCount!: number;
  public geoPosition!: string;
  public children!: OTObject[];
  public picture!: string;
  public thumbnail!: string;
  public curLayer!: string;
  public nextLayer!: string;
  public layerName!: string;
  public layerID!: number;
  public buildingPlan!: string;
  public buildingPlanName!: string;
  public numSensors?: number; // EM: number of sensors on equipment
  public sensors?: Sensor[] = [];
  public delete?: boolean;
  public structureFloorZone?: boolean;
  public structureSectionCrossing?: boolean;
  public structureAssociatedEquipment?: boolean;
  public displayOrder?: number;
  public customerName!: string;
  public sensorDigitIDs!: string;
  public edgeDeviceIDs!: string;
  public macs!: string;
  public gatewayIDs!: string;
  public defaultImageId?: string;
}

// EM: Asset with geo coordinates
export class AssetGeoData extends OTObject {
  public latitude!: number;
  public longitude!: number;
}

// EM: Country model
export class Country {
  public id!: number;
  public name!: {
    en: string;
    de: string;
  };
}

// EM: Notification/alert for sensor measurements
export class Notification {
  public notificationID!: number;
  public title!: string;
  public message!: string;
  public instructions!: string;
  public measurementTypeID!: number;
  public measurementTitle!: string;
  public objectID!: number;
  public notificationStatusID!: number;
  public notificationStatus!: string;
  public objectName!: string;
  public sensorName!: string;
  public sensorID!: number;
  public equipmentType!: string;
  public colorCode!: string;
  public isWarning!: boolean;
  public isCritical!: boolean;
  public inactive!: boolean;
  public isArchived!: boolean;
  public severity!: number;
  public value!: number;
  public prevValue!: number;
  public measuredValue!: string;
  public isEdgeDevice!: boolean;
  public timestamp!: Date;
  public breadcrumbs: OTObject[] = [];
  public expanded = true;
  public onlyApplyWorseStatus!: boolean;
  public notifyAllChanges!: boolean;
  public unit?: string;

  constructor(data: Partial<Notification>, navs: OTObject[]) {
    Object.assign(this, data);
    // Ensure isEdgeDevice is always a boolean (default to false if not provided)
    if (typeof this.isEdgeDevice !== 'boolean') {
      this.isEdgeDevice = false;
    }
    this.gatherBreadcrumbs(navs);
  }

  private gatherBreadcrumbs(navigation: OTObject[]) {
    this.breadcrumbs = [];
    const currentID = this.objectID;
    let nav = navigation.find((_nav) => _nav.objectID === currentID);
    let iterations = 0;
    const max = 20;
    while (!!nav && iterations < max) {
      iterations++;
      this.breadcrumbs = [
        {
          objectID: nav.objectID,
          objectName: nav.objectName,
          objectTypeID: nav.objectTypeID,
          objectType: nav.objectType,
          parentID: nav.parentID,
          objectStatus: nav.objectStatus,
          colorCode: nav.colorCode,
          isAsset: nav.isAsset,
          isConnected: nav.isConnected,
          isLifetimeConnected: nav.isLifetimeConnected,
          description: null as any,
          warning: nav.warning,
          critical: nav.critical,
          inactive: nav.inactive,
          isArchived: nav.isArchived,
          equipmentType: nav.equipmentType,
          childCount: nav.childCount,
          geoPosition: nav.geoPosition,
          children: nav.children,
          picture: nav.picture,
          thumbnail: nav.thumbnail,
          curLayer: nav.curLayer,
          nextLayer: nav.nextLayer,
          layerName: nav.layerName,
          layerID: nav.layerID,
          buildingPlan: nav.buildingPlan,
          buildingPlanName: nav.buildingPlanName,
          numSensors: nav.numSensors,
          customerName: nav.customerName,
          edgeDeviceIDs: nav.edgeDeviceIDs,
          sensorDigitIDs: nav.sensorDigitIDs,
          macs: nav.macs,
          gatewayIDs: nav.gatewayIDs,
          defaultImageId: nav.defaultImageId,
          sensors: nav.sensors,
        },
        ...this.breadcrumbs,
      ];
      nav = navigation.find((_nav) => _nav.objectID === nav?.parentID);
    }
  }
}

// EM: Sensor threshold configuration
export class SensorThreshold {
  public sensorID!: number;
  public sensorMeasurementTypeID!: number;
  public thresholdMin!: number;
  public thresholdMax!: number;
  public warning!: boolean;
  public error!: boolean;
}
